import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { isFiniteNumber } from '@core';

import { cameraViewMapAtom } from '../activity/camera';

import { activeAnalysisAtom, activeVideoAtom, comparisonAnalysisAtom, comparisonVideoAtom } from './swings';

export const MAX_PROGRESS = 100;
export const FIRST_POSITION = 'P1';

/**
 * Numeric value from 0 to 100;
 *
 * Indicates progress of the scrubber.
 */
export const progressAtom = atomWithReset(0);

/**
 * Controls for scrubber play/stop.
 */
export const autoplayEnabledAtom = atomWithReset(false);

/**
 * Main (active swing) video length in frames.
 * Return 1, if empty to avoid division by 0.
 */
export const totalFramesAtom = atom((get) => {
    const activeVideo = get(activeVideoAtom);
    return activeVideo?.[0]?.metadata.totalFrames ?? 1;
});

/**
 * Shared activity-level playback lock.
 */
export const scrubberLockAtom = atomWithReset<boolean>(false);
export const pendingScrubberLockAtom = atomWithReset<{ position: string; sourceSwingId: string } | null>(null);

/**
 * Secondary (comparison swing) video length in frames.
 * Return 1, if empty to avoid division by 0.
 */
export const comparisonTotalFrames = atom((get) => {
    const comparisonVideo = get(comparisonVideoAtom);
    return comparisonVideo?.[0]?.metadata.totalFrames ?? 1;
});

/**
 * An active swing's map of each P position segments  in frames (or null if no segment).
 * We use this to track where each P position happens in swing.
 *
 * Example:
 * ```
 * {
 *    "P1": 10,
 *    "P2": 32,
 *    "P3": null,
 *    ...
 * }
 * ```
 * To get this in progress values: `segmentFrames / totalFrames * 100`.
 */
export const activeSegmentationsMapAtom = atom<Record<string, number> | undefined>((get) => {
    const swingAnalysis = get(activeAnalysisAtom);
    const analysis = swingAnalysis?.fullAnalysis ?? swingAnalysis?.quickAnalysis;
    return analysis?.data?.analysis?.segmentation;
});

/**
 * Same thing as active swing segments, just for a comparison swing.
 */
export const comparisonSegmentationsMapAtom = atom<Record<string, number> | undefined>((get) => {
    const swingAnalysis = get(comparisonAnalysisAtom);
    const analysis = swingAnalysis?.fullAnalysis ?? swingAnalysis?.quickAnalysis;
    return analysis?.data?.analysis?.segmentation;
});

/**
 * Tuple list: `["P10", 123][]` - P positions and segmentation frames.
 * This is a segmentation list that is in order and can be used in loops to find nearest P position by frame.
 *
 * See: `currentPositionAtom`
 */
export const activeSegmentationsAtom = atom((get) => {
    const activeSegmentationsMap = get(activeSegmentationsMapAtom);
    return Object.entries(activeSegmentationsMap ?? {})
        .filter(([, frame]) => isFiniteNumber(frame))
        .sort(([, a], [, b]) => a - b);
});

/**
 * Video segmentations difference between active and comparison swing.
 * This can be used to sync P positions between swings.
 *
 * Each swing can have different frame intervals between each P position,
 * as well as we might not have a segmentation frames for some positions, in that case we have diff as 0.
 *
 * In addition P1 is always 0, and is a valid diff.
 *
 * Later on we validate the diff by checking if it's not 0 OR is P1:
 * `const isValid = segmentationDiffMap[pos] || STARTING_POSITION === pos;`
 *
 * Example:
 * ```
 * {
 *    "P1": 0,
 *    "P2": -10,
 *    "P4": 2,
 *    ...
 * }
 * ```
 */
export const segmentationsDiffMapAtom = atom<Record<string, number>>((get) => {
    const activeSegmentations = get(activeSegmentationsAtom);
    const comparisonSegmentationsMap = get(comparisonSegmentationsMapAtom);

    const segmentationDiffEntries: [string, number][] = activeSegmentations.map(([position, frame]) => {
        const comparisonFrame = comparisonSegmentationsMap?.[position];
        if (comparisonFrame) {
            return [position, frame - comparisonFrame];
        }
        return [position, 0];
    });

    return Object.fromEntries(segmentationDiffEntries);
});

/**
 * Global state for a comparison video position sync.
 * By default, all videos are tracked from beginning and can vary in length,
 * which means they are synced at the start ("P1", position).
 *
 * If we apply an offset, which we get from segmentationDiffMap
 *
 */
export const syncedPositionAtom = atomWithReset<string>(FIRST_POSITION);

/**
 * Selector that combines segmentation diff map and synced position.
 * This is not used directly, but as an intermediate value for `syncedFrameAtom`.
 *
 * This is the reason why it's not exported.
 */
const _syncedPositionFrameDiffAtom = atom((get) => {
    const syncedPosition = get(syncedPositionAtom);
    if (!syncedPosition || syncedPosition === FIRST_POSITION) return 0;
    const segmentationsDiffMap = get(segmentationsDiffMapAtom);
    const frameDiff = segmentationsDiffMap[syncedPosition];
    return frameDiff || 0;
});

/**
 * Current frame to display in video playback.
 */
export const currentFrameAtom = atom((get) => {
    const progress = get(progressAtom);
    const totalFrames = get(totalFramesAtom);
    return Math.min(Math.round((progress * totalFrames) / MAX_PROGRESS), totalFrames);
});

/**
 * Synced/comparison frame to display in video playback.
 */
export const syncedFrameAtom = atom((get) => {
    const currentFrame = get(currentFrameAtom);
    const syncedPositionFrameDiff = get(_syncedPositionFrameDiffAtom);
    return Math.max(currentFrame - syncedPositionFrameDiff, 0);
});

/**
 * Current P position that was reached
 */
export const currentPositionAtom = atom((get) => {
    const segmentations = get(activeSegmentationsAtom);
    const currentFrame = get(currentFrameAtom);
    return segmentations.reduce<string>(
        (prevPosition, [position, positionFrame]) => (currentFrame >= positionFrame ? position : prevPosition),
        FIRST_POSITION,
    );
});

export const currentPositionNumberAtom = atom((get) => {
    const currentPosition = get(currentPositionAtom);
    const positionNumber = Number(currentPosition.substring(1));
    return positionNumber;
});

// Camera views are 1 | 2 | 3 | 4, so 1 is the first and default one.
export const cameraViewAtom = atomWithReset<number | null>(null);
export const playbackSpeedAtom = atomWithReset(1);

export const cameraViewsAtom = atomWithReset<[number, number, number, number]>([1, 2, 3, 4]);

// Tracks when user explicitly selects a P position from PlaybackControls.
export const selectedPositionAtom = atomWithReset<string | null>(null);

export const selectedCameraAtom = atom((get) => {
    const cameraViewMap = get(cameraViewMapAtom);
    const cameraView = get(cameraViewAtom);
    return cameraView ? cameraViewMap[cameraView] : null;
});

export const defaultCameraAtom = atom((get) => {
    const cameraViewMap = get(cameraViewMapAtom);
    return cameraViewMap[1];
});
